import {
    CorporationUserRole,
    ILocationResponse,
    IUserResponse,
    LocationUserRole,
    NotificationType,
} from '@kcftech/prometheus-client';
import { User } from 'components/dropdown/UserDropdown';
import { isKcfAdmin } from 'pages/Desk/issues/shared';
import { unacknowledgedPeriodRules } from 'pages/Settings/NotificationSubscriptions/form/IndicatorAlertsNotificationSubscriptionFormContent';
import { CreateNotificationSubscriptionFormData } from 'pages/Settings/NotificationSubscriptions/form/types';
import { FieldErrors } from 'react-hook-form';

export type NotificationSubscriptionFormErrors = Readonly<{
    /**
     * General form error (not tied to any specific field). Value has no
     * meaning in the form, hence the `never` type.
     */
    formError?: never;
}>;

export type FormValues = CreateNotificationSubscriptionFormData &
    NotificationSubscriptionFormErrors;

/**
 * Retrieves the notification triggers based on the form type.
 * @param form The form data to retrieve notification triggers from.
 * @returns Notification triggers based on the form type.
 * @throws Error if the form type is not recognized.
 */
const getNotificationTriggers = (form: CreateNotificationSubscriptionFormData) => {
    switch (form.type) {
        case NotificationType.Indicator:
            return form.indicatorAlerts?.notificationTriggers;
        case NotificationType.Hardware:
            return form.hardware?.notificationTriggers;
        case NotificationType.MachineHealth:
            return form.machineHealth?.notificationTriggers;
        default:
            throw new Error(
                `form NotificationType ('${form.type}') does not have notification trigger validation configured.`,
            );
    }
};

/**
 * Returns true if at least one notification type has some notification method turned on.
 * @param form The form data to check.
 * @throws Error if the form type is not recognized.
 */
export const hasAtLeastOneNotificationType = (
    form: CreateNotificationSubscriptionFormData,
) => {
    // TODO: email is always turned on for hardware health notifications. Update validation when push is added.
    if (form.type === NotificationType.HardwareHealth) {
        return true;
    }

    const notificationTriggers = getNotificationTriggers(form);

    if (notificationTriggers === undefined) {
        throw new Error('how did this even happen?');
    }

    return Object.values(notificationTriggers).some((notificationType) =>
        Object.values(notificationType).includes(true),
    );
};

export const validateSubscriptionUsers = (
    form: CreateNotificationSubscriptionFormData,
) => {
    if (form.users.length === 0) {
        return {
            users: [
                {
                    Id: {
                        type: 'required',
                        message: 'At least one user must be selected',
                    },
                },
            ],
        };
    }

    if (hasPhoneNumberError(form, form.users)) {
        return {
            notificationTriggerPhoneNumber: {
                type: 'validate',
                message:
                    'One or more users do not have a valid phone number. Please see the user list below for more information.',
            },
        };
    }

    if (hasNonKCFUserError(form, form.users)) {
        return {
            notificationTriggerNonKCFUsers: {
                type: 'validate',
                message:
                    'One or more users have insufficient permissions to view Draft Issues. Deselect those users, or uncheck the "Draft Issues" checkboxes.',
            },
        };
    }

    return undefined;
};

/**
 * Validates an object of form data for creating/editing a notification subscription
 * and returns all errors found. If no errors were found, returns the object
 * of correct values, otherwise no values will be returned. Compatible with
 * the `useForm` resolver.
 * @param form The form data to validate.
 */
export const validateSubscriptionForm = (
    form: CreateNotificationSubscriptionFormData,
) => {
    const errors: FieldErrors<FormValues> = {
        ...(!hasAtLeastOneNotificationType(form) && {
            notificationTriggers: [
                {
                    type: 'validate',
                    message: 'Please enable at least one alert type.',
                },
            ],
        }),
        ...(!form.location && {
            location: {
                type: 'required',
                message: 'Please select a location.',
            },
        }),
        ...validateSubscriptionUsers(form),
        ...(form.type === NotificationType.HardwareHealth &&
            (form.hardwareIssues === undefined ||
                form.hardwareIssues.faultTypes.length === 0) && {
                hardwareIssues: {
                    faultTypes: {
                        type: 'validate',
                        message:
                            'Please select at least one fault type to receive notifications for',
                    },
                },
            }),
        ...(form.type === NotificationType.Indicator &&
            // resolvers run incrementally on every prop on the form object, but
            // the object gets constructed one prop at a time (the validation
            // happens as it is being constructed), so we need to specify to not
            // run the validator if unacknowledgedPeriod has not yet been
            // populated
            form.indicatorAlerts?.unacknowledgedPeriodMs &&
            mapIfDefined(
                unacknowledgedPeriodRules.validate(
                    form.indicatorAlerts.unacknowledgedPeriodMs,
                ),
                (message) => ({
                    indicatorAlerts: {
                        unacknowledgedPeriodMs: {
                            type: 'validate',
                            message,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any,
                    },
                }),
            )),
    };

    return {
        values: Object.values(errors).length === 0 ? form : {},
        errors,
    };
};

/**
 * Returns true if at least one notification type has SMS notifications turned on.
 * @param form The form data to check.
 * @returns True if at least one notification type has SMS notifications turned on, otherwise false.
 * @throws Error if the form type is not recognized.
 */
export const hasTextEnabled = (form: CreateNotificationSubscriptionFormData) => {
    // Hardware health subscriptions do not support SMS
    if (form.type === NotificationType.HardwareHealth) {
        return false;
    }

    const notificationTriggers = getNotificationTriggers(form);

    if (notificationTriggers === undefined) {
        throw new Error(
            'Missing notification triggers on form data. This should not happen.',
        );
    }

    return Object.values(notificationTriggers).some(
        (notificationType) => notificationType.IsSmsEnabled,
    );
};

export const hasDraftNotificationsEnabled = (
    form: CreateNotificationSubscriptionFormData,
) => {
    const draftTriggers = form.machineHealth?.notificationTriggers.DraftIssueCreate;

    if (draftTriggers === undefined) {
        throw new Error(
            'Missing draft notification triggers on form data. This should not happen.',
        );
    }

    return draftTriggers.IsSdConnectPushEnabled || draftTriggers.IsEmailEnabled;
};

export const notificationSubscriptionFormError = {
    userHasNoPhoneNumber:
        'Valid phone number is required. Phone number can be added/edited from user profile.',
};

const isAdminUserForLocation = (user: User, location: ILocationResponse | undefined) => {
    if (isKcfAdmin(user.ApplicationRoleCode)) {
        return true;
    }

    if (location === undefined) {
        return false;
    }

    return (
        user.Corporations.some(
            (corp) =>
                corp.CorporationUserRole === CorporationUserRole.Admin &&
                corp.CorporationId === location.Corporation.CorporationId,
        ) ||
        user.Locations.some(
            (loc) =>
                loc.LocationUserRole === LocationUserRole.Admin &&
                loc.LocationId === location.Id,
        )
    );
};

export const canViewDraftIssues = (user: User, location: ILocationResponse | undefined) =>
    isKcfAdmin(user.ApplicationRoleCode) ||
    (location?.Settings.DraftIssuesAvailableToCustomerAdmins &&
        isAdminUserForLocation(user, location));

const hasPhoneNumberError = (
    form: CreateNotificationSubscriptionFormData,
    users: Pick<IUserResponse, 'PhoneNumber'>[],
) => {
    if (hasTextEnabled(form)) {
        return users.some((user) => !user.PhoneNumber);
    }

    return undefined;
};

const hasNonKCFUserError = (
    form: CreateNotificationSubscriptionFormData,
    users: User[],
) => {
    if (hasDraftNotificationsEnabled(form)) {
        return users.some((user) => !canViewDraftIssues(user, form.location));
    }

    return undefined;
};

/**
 * Returns `undefined` if `val` is `null` or `undefined`, otherwise calls
 * `mapFn` on `val` and returns the result.
 */
const mapIfDefined = <T, U>(
    val: T | undefined | null,
    mapFn: (v: T) => U,
): U | undefined => (val !== undefined && val !== null ? mapFn(val) : undefined);
